import React, { FunctionComponent, useEffect, useState } from 'react';
import { ZiggeoRecorder } from 'react-ziggeo';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import { ZIGGEO_API_KEY } from 'src/config/common.constants';
import { sendVideoMail } from 'src/services/video-mail.service';
import styled from 'styled-components';
import { CircularProgress } from '@mui/material';
import { useToast } from '../../../context/ToastContext';

const StyledVideoRecorder = styled.div`
    overflow: hidden;
    margin: 20px;
    width: 100%;
`;

const StyledLoader = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    top: 50%;
    height: 800px;
`;

export interface VideoRecorderDialogProps {
    handleCloseClick?: () => void;
    showVideoDialog: boolean;
    requestId: string | null;
}

const VideoRecorderDialog: FunctionComponent<VideoRecorderDialogProps> = ({
    handleCloseClick,
    showVideoDialog,
    requestId,
}) => {
    const toast = useToast();
    const [recorder, setRecorder] = useState<any>(null);
    const [loadingFlag, setLoadingFlag] = useState(true);

    const videoWidth = '82.5vh';

    useEffect(() => {
        setTimeout(() => {
            if (requestId) {
                setLoadingFlag(false);
            }
        }, 1000);
    }, [requestId]);

    useEffect(() => {
        if (recorder && requestId) {
            recorder.on('verified', async () => {
                const videoToken: string = recorder.get('video');
                const durationInMilliSeconds: number = recorder.get('duration');
                const { error, data } = await sendVideoMail({
                    requestId,
                    videoToken,
                    durationInMilliSeconds,
                });

                if (error) {
                    toast.error(error);
                }

                if (data) {
                    toast.success('Videomail sent successfully.');
                    handleCloseClick?.();
                }
            });
        }
    }, [recorder]);

    const handleCameraError = () => {
        toast.error('please check your camera and try again');
    };

    const onCameraUnresponsive = () => {
        toast.error('please check your camera and try again');
    };

    return (
        <>
            <div>
                <Dialog
                    open={showVideoDialog}
                    onClose={handleCloseClick}
                    aria-labelledby="draggable-dialog-title"
                    fullScreen
                >
                    <>
                        <AppBar
                            sx={{
                                position: 'relative',
                                backgroundColor: '#1d1d1d',
                            }}
                        >
                            <Toolbar>
                                <Typography variant="h6" sx={{ marginLeft: 2, flex: 1 }}>
                                    Videomail Recorder
                                </Typography>
                                <IconButton
                                    color="inherit"
                                    onClick={handleCloseClick}
                                    aria-label="close"
                                >
                                    <CloseIcon />
                                </IconButton>
                            </Toolbar>
                        </AppBar>
                        <DialogContent
                            style={{
                                backgroundColor: '#1d1d1d',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            {loadingFlag ? (
                                <StyledLoader>
                                    <CircularProgress size={100} />
                                </StyledLoader>
                            ) : (
                                <StyledVideoRecorder>
                                    <ZiggeoRecorder
                                        apiKey={ZIGGEO_API_KEY}
                                        height={videoWidth}
                                        width="100%"
                                        onRef={(ref: any) => setRecorder(ref)}
                                        recordingwidth={640}
                                        recordingheight={480}
                                        theme="modern"
                                        themecolor="blue"
                                        picksnapshots={false}
                                        pickcovershotframe="false"
                                        skipinitial
                                        onCameraUnresponsive={onCameraUnresponsive}
                                        onError={handleCameraError}
                                    />
                                </StyledVideoRecorder>
                            )}
                        </DialogContent>
                    </>
                </Dialog>
            </div>
        </>
    );
};

export default VideoRecorderDialog;
